/**
 * iter150AZ.76 — Resolve the Sequencing Live App base URL with a
 * build-time → runtime fallback.
 *
 * Priority:
 *   1. ``process.env.REACT_APP_LIVE_APP_BASE_URL`` — inlined at build
 *      time by CRA. Fast, no network call. The "happy path".
 *   2. Runtime lookup: ``GET /api/sequencing/live-app-base-url``.
 *      Reads ``LIVE_APP_BASE_URL`` from the backend env at request
 *      time, so deployments can configure it without rebuilding the
 *      frontend bundle.
 *
 * Returns ``{ baseUrl, loading, configured }``. ``baseUrl`` is
 * always trimmed and has no trailing slash. ``configured`` flips to
 * ``true`` as soon as either source produces a value.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const _API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const _ax  = axios.create({ withCredentials: true });

// Module-level cache so we don't refetch on every component mount.
let _cached = null;
let _inflight = null;

function _normalise(s) {
  return (s || "").toString().trim().replace(/\/+$/, "");
}

function _initialRuntime() {
  return _cached;
}

export function useLiveAppBaseUrl() {
  const buildTime = _normalise(process.env.REACT_APP_LIVE_APP_BASE_URL);
  const [runtime, setRuntime] = useState(_initialRuntime);
  const [loading, setLoading] = useState(
    !buildTime && _cached === null,
  );

  useEffect(() => {
    if (buildTime) return undefined;                 // build-time wins
    if (_cached !== null) return undefined;          // already resolved
    let cancelled = false;
    if (!_inflight) {
      _inflight = _ax
        .get(`${_API}/sequencing/live-app-base-url`)
        .then((r) => _normalise(r.data?.base_url))
        .catch(() => "")
        .finally(() => { _inflight = null; });
    }
    _inflight.then((v) => {
      if (cancelled) return;
      _cached = v;
      setRuntime(v);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [buildTime]);

  const baseUrl = buildTime || runtime || "";
  return { baseUrl, loading, configured: !!baseUrl };
}
